import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Switch,
  Tab,
  Tabs,
  alpha,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../CoreDataService";
import axios from "axios";
import { useSelector } from "react-redux";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";
import CreateSingleBotModal from "./CreateSingleBotModal";

const RegularBotColumns = [
  "Bot ID", "Exchange", "Name", "Description", "Market",
  "Pair", "Order", "Order Type", "Bot Type", "Expires In",
  "Is Active", "Actions",
];

const SignalBotColumns = [
  "Bot ID", "Name", "Description", "Exchange", "Pair",
  "Amount/Trade", "Amount Type", "Order Type", "Start Condition",
  "Take Profit", "Stop Loss", "Max Trades", "Max Daily Capital",
  "Status", "Actions",
];

const COPYTRADINGSERVICE = "https://accounts.paybito.com/api/copyTrading";

const MyBotsNew = () => {
  const { btncolor } = useSelector((state) => state);
  const navigate = useNavigate();

  // Regular bots state
  const [allBots, setAllBots] = useState([]);
  const [noOfRecords, setNoOfRecords] = useState(0);
  const [pgn, setPgn] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [itemNo, setItemNo] = useState(10);
  const [botStatus, setBotStatus] = useState(0);

  // Signal bots state
  const [signalBots, setSignalBots] = useState([]);
  const [signalNoOfRecords, setSignalNoOfRecords] = useState(0);
  const [signalPgn, setSignalPgn] = useState([]);
  const [signalPageNo, setSignalPageNo] = useState(1);
  const [signalItemNo, setSignalItemNo] = useState(10);

  // Modal state
  const [singleBotModalOpen, setSingleBotModalOpen] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const getAllBots = () => {
    const payload = { customerId: parseInt(localStorage.getItem("userId")), botId: 0, pageNo, itemNo };
    axios
      .post(`${COPYTRADINGSERVICE}/bot/GetBotDetails`, payload, {
        headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") },
      })
      .then((response) => {
        const result = response.data;
        setAllBots(result.botDetails || []);
        setNoOfRecords(result.no_of_records);
        const totalPages = Math.ceil(result.no_of_records / itemNo);
        setPgn(Array.from({ length: totalPages }, (_, i) => i + 1));
      })
      .catch(() => { showToast("error", "Internal Server Error"); });
  };

  const getSignalBots = () => {
    const payload = { uuid: localStorage.getItem("uuid"), customerId: parseInt(localStorage.getItem("userId")), exchange: "PAYBITO", pageNo: signalPageNo, itemNo: signalItemNo };
    axios
      .post(`${COPYTRADINGSERVICE}/bot/getSignalBotDetails`, payload, {
        headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") },
      })
      .then((response) => {
        const result = response.data;
        setSignalBots(result.botDetails || []);
        setSignalNoOfRecords(result.no_of_records || 0);
        const totalPages = Math.ceil((result.no_of_records || 0) / signalItemNo);
        setSignalPgn(Array.from({ length: totalPages }, (_, i) => i + 1));
      })
      .catch(() => { showToast("error", "Failed to fetch signal bots"); });
  };

  useEffect(() => {
    if (activeTab === 0) getAllBots();
    else getSignalBots();
  }, [activeTab, pageNo, itemNo, signalPageNo, signalItemNo]);

  const changeBotIsActiveStatus = async (event, botId) => {
    const newStatus = event.target.checked ? 1 : 0;
    setBotStatus(newStatus);
    try {
      const response = await axios.get(
        `${COPYTRADINGSERVICE}/bot/UpdateBotDetails/${botId}/${newStatus}`,
        { headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") } }
      );
      if (response.data.error.error_data !== 0) showToast("error", response.data.error.error_msg);
      else { showToast("success", response.data.error.error_msg); getAllBots(); }
    } catch { showToast("error", "Something went wrong!"); }
  };

  const pauseBot = async (id, status) => {
    try {
      const response = await axios.post(
        `${COPYTRADINGSERVICE}/bot/PauseResumeBot`,
        JSON.stringify({ botId: id, action: status }),
        { headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") } }
      );
      const res = response.data;
      if (res.error.error_data !== 0) showToast("error", res.error.error_msg);
      else showToast("success", res.error.error_msg);
      getAllBots();
    } catch { showToast("error", "Internal Server Error"); }
  };

  const gotoEditBot = (bot) => { navigate("/edit-bot", { state: { bot } }); };

  const deleteBot = (id) => {
    if (!window.confirm("Are you sure you want to delete the bot?")) return;
    axios
      .post(`${COPYTRADINGSERVICE}/bot/DeleteBotDetails`, { botId: id }, {
        headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") },
      })
      .then((response) => {
        const res = response.data;
        if (res.error?.error_data === 0) showToast("success", res.error.error_msg);
        else showToast("error", res.error?.error_msg || "Something went wrong");
        getAllBots();
      })
      .catch(() => { showToast("error", "Internal Server Error"); });
  };

  const editSignalBot = (bot) => { setEditingBot(bot); setSingleBotModalOpen(true); };

  const deleteSignalBot = (bot) => {
    if (!window.confirm("Are you sure you want to delete this signal bot?")) return;
    const payload = {
      customerId: parseInt(localStorage.getItem("userId")),
      uuid: localStorage.getItem("uuid"),
      botId: bot.botId, action: "DELETE",
      name: bot.name, description: bot.description, exchange: bot.exchange,
      apiKeyId: bot.apiKeyId, assetPair: bot.assetPair, startCondition: bot.startCondition,
      orderType: bot.orderType, amountPerTrade: bot.amountPerTrade, amountType: bot.amountType,
      isTpEnabled: bot.isTpEnabled, tp1TargetPct: bot.tp1TargetPct, tp2TargetPct: bot.tp2TargetPct,
      tp1PortfolioPct: bot.tp1PortfolioPct, tp2PortfolioPct: bot.tp2PortfolioPct,
      isSlEnabled: bot.isSlEnabled, slTargetPct: bot.slTargetPct,
      maxTrades: bot.maxTrades, maxDailyCapitalExposure: bot.maxDailyCapitalExposure,
    };
    axios
      .post(`${COPYTRADINGSERVICE}/bot/createSignalBot`, JSON.stringify(payload), {
        headers: { "Content-Type": "application/json", authorization: "BEARER " + localStorage.getItem("access_token") },
      })
      .then((response) => {
        const res = response.data;
        if (res.error?.error_data === 0) { showToast("success", res.error.error_msg || "Deleted"); getSignalBots(); }
        else showToast("error", res.error?.error_msg || "Something went wrong");
      })
      .catch(() => { showToast("error", "Internal Server Error"); });
  };

  const handleModalClose = () => { setSingleBotModalOpen(false); setEditingBot(null); };
  const handleBotCreated = () => { getSignalBots(); };
  const handleTabChange = (event, newValue) => { setActiveTab(newValue); };

  const formatTakeProfit = (bot) => {
    if (bot.isTpEnabled === 0) return "Disabled";
    return `${bot.tp1TargetPct}%${bot.tp2TargetPct ? `, ${bot.tp2TargetPct}%` : ""}`;
  };
  const formatStopLoss = (bot) => {
    if (bot.isSlEnabled === 0) return "Disabled";
    return `${bot.slTargetPct}%`;
  };
  const getBotStatus = (bot) => {
    if (bot.botIsDeleted === 1) return "Deleted";
    return bot.botIsRunning === 1 ? "Running" : "Stopped";
  };

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <SmartToyOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={heroTitle}>My Bots</Typography>
            <Typography sx={heroSubtitle}>
              Manage your trade automation bots and signal bots
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <ContainedButton
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => navigate("/create-new-bot")}
              sx={actionBtn}
            >
              New Bot
            </ContainedButton>
            <ContainedButton
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => { setEditingBot(null); setSingleBotModalOpen(true); }}
              sx={actionBtn}
            >
              Signal Bot
            </ContainedButton>
            <ContainedButton
              variant="contained"
              onClick={() => navigate("/copy-trade")}
              sx={backBtn}
            >
              Back
            </ContainedButton>
          </Box>
        </Box>
      </Box>

      {/* Main Card */}
      <Box sx={mainCard}>
        {/* Tabs */}
        <Box sx={tabsWrap}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ...tabsStyle,
              "& .MuiTabs-indicator": { backgroundColor: btncolor, height: 3, borderRadius: "3px 3px 0 0" },
              "& .Mui-selected": { color: `${btncolor} !important` },
            }}
          >
            <Tab label="Trade Automation Bots" sx={tabItem} />
            <Tab label="Signal Bots" sx={tabItem} />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <TableContainer component={Paper} sx={modernTableContainer}>
            <Table sx={tableStyle} stickyHeader>
              <TableHead>
                <TableRow sx={modernTableHeadRow}>
                  {(activeTab === 0 ? RegularBotColumns : SignalBotColumns).map((col, i) => (
                    <TableCell key={i} sx={modernTableHeadCell}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeTab === 0 ? (
                  allBots?.length > 0 ? (
                    allBots.map((bot, idx) => (
                      <TableRow key={idx} sx={modernTableBodyRow}>
                        <TableCell sx={modernTableBodyCell}>{bot.botId}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.exchange_name}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botName}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botDescription}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botMarketType}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botAssetPair}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botTradeOrder}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botOrderType}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.botType}</TableCell>
                        <TableCell sx={modernTableBodyCell}>
                          {bot.bot_is_expired === 0 ? (
                            <Typography sx={{ fontSize: "0.8rem", color: "var(--text-color)" }}>
                              {bot.days_left_to_expire}d {bot.hours_left_to_expire}h {bot.mins_left_to_expire}m
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: "0.75rem", color: "#ef4444" }}>
                              Expired {bot.bot_expired_on}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={modernTableBodyCell}>
                          {bot.bot_is_deleted === 1 && (
                            <Typography sx={{ fontSize: "0.75rem", color: "#ef4444" }}>
                              Deleted {bot.bot_deleted_on}
                            </Typography>
                          )}
                          {bot.bot_is_expired === 0 && bot.bot_is_deleted !== 1 && (
                            <Switch
                              checked={bot.botIsActive === 1}
                              onChange={(e) => changeBotIsActiveStatus(e, bot.botId)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: btncolor },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: btncolor },
                              }}
                            />
                          )}
                          {bot.bot_is_expired === 1 && bot.bot_is_deleted !== 1 && <span>N/A</span>}
                        </TableCell>
                        <TableCell sx={{ ...modernTableBodyCell, whiteSpace: "nowrap" }}>
                          {bot.bot_is_expired === 0 && bot.bot_is_deleted !== 1 ? (
                            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center", flexWrap: "wrap" }}>
                              {bot.botIsRunning === 1 ? (
                                <ContainedButton size="small" onClick={() => pauseBot(bot.botId, "PAUSE")} sx={tableActionBtn}>
                                  Pause
                                </ContainedButton>
                              ) : (
                                <ContainedButton size="small" onClick={() => pauseBot(bot.botId, "RESUME")} sx={tableActionBtn}>
                                  Resume
                                </ContainedButton>
                              )}
                              <OutlinedButton size="small" variant="outlined" onClick={() => gotoEditBot(bot)} sx={tableActionBtn}>
                                Edit
                              </OutlinedButton>
                              <OutlinedButton size="small" variant="outlined" onClick={() => deleteBot(bot.botId)} sx={{ ...tableActionBtn, borderColor: "#ef4444", color: "#ef4444" }}>
                                Delete
                              </OutlinedButton>
                            </Box>
                          ) : <span>N/A</span>}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} sx={emptyCell}>
                        <InboxOutlinedIcon sx={emptyIcon} />
                        <Typography sx={emptyText}>No trade automation bots found</Typography>
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  signalBots?.length > 0 ? (
                    signalBots.map((bot, idx) => (
                      <TableRow key={idx} sx={modernTableBodyRow}>
                        <TableCell sx={modernTableBodyCell}>{bot.botId}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.name}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.description}</TableCell>
                        <TableCell sx={modernTableBodyCell}>
                          {bot.exchange === "PAYBITO" ? localStorage.getItem("exchange") : bot.exchange}
                        </TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.assetPair}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.amountPerTrade}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.amountType}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.orderType === 1 ? "Market" : "Limit"}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.startCondition}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{formatTakeProfit(bot)}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{formatStopLoss(bot)}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.maxTrades || "∞"}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{bot.maxDailyCapitalExposure || "∞"} {bot.amountType}</TableCell>
                        <TableCell sx={modernTableBodyCell}>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: "6px",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              backgroundColor:
                                bot.botIsDeleted === 1
                                  ? "rgba(239,68,68,0.1)"
                                  : bot.botIsRunning === 1
                                  ? "rgba(22,163,74,0.1)"
                                  : "rgba(234,179,8,0.1)",
                              color:
                                bot.botIsDeleted === 1
                                  ? "#ef4444"
                                  : bot.botIsRunning === 1
                                  ? "#16a34a"
                                  : "#ca8a04",
                            }}
                          >
                            {getBotStatus(bot)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...modernTableBodyCell, whiteSpace: "nowrap" }}>
                          {bot.botIsDeleted !== 1 ? (
                            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center" }}>
                              <OutlinedButton size="small" variant="outlined" onClick={() => editSignalBot(bot)} sx={tableActionBtn}>
                                Edit
                              </OutlinedButton>
                              <OutlinedButton size="small" variant="outlined" onClick={() => deleteSignalBot(bot)} sx={{ ...tableActionBtn, borderColor: "#ef4444", color: "#ef4444" }}>
                                Delete
                              </OutlinedButton>
                            </Box>
                          ) : <span>N/A</span>}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} sx={emptyCell}>
                        <InboxOutlinedIcon sx={emptyIcon} />
                        <Typography sx={emptyText}>No signal bots found</Typography>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <CreateSingleBotModal
        open={singleBotModalOpen}
        onClose={handleModalClose}
        onBotCreated={handleBotCreated}
        editBotData={editingBot}
      />
    </Box>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
  flexWrap: "wrap",
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 540,
};

const actionBtn = {
  height: 40,
  px: 2,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.82rem",
  flexShrink: 0,
};

const backBtn = {
  height: 40,
  px: 2.5,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
  flexShrink: 0,
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.3)",
  color: "#fff",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const tabsStyle = {
  minHeight: 56,
  "& .MuiTab-root": { minHeight: 56 },
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  letterSpacing: "0.01em",
  px: 2,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 900,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.5,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
};

const tableActionBtn = {
  borderRadius: "7px",
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  px: 1.5,
  height: 30,
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

export default MyBotsNew;
